<template>
  <div class="transactions-page">
    <!-- Header Section -->
    <div class="page-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1 class="h3 mb-1 text-dark fw-bold">Transactions</h1>
          <p class="text-muted mb-0">{{ total.toLocaleString() }} total transactions</p>
        </div>
        <div class="d-flex gap-2">
          <button 
            class="btn btn-outline-dark"
            @click="toggleFilters"
          >
            <i class="fas fa-filter me-1"></i>
            Filters
          </button>
          <button class="btn btn-dark">
            <i class="fas fa-download me-1"></i>
            Export
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Filters Sidebar -->
      <div class="col-lg-3 col-md-12 mb-4" v-show="showFilters">
        <div class="card shadow-sm">
          <div class="card-header bg-light">
            <h6 class="mb-0">
              <i class="fas fa-search me-2"></i>
              Search & Filter
            </h6>
          </div>
          <div class="card-body">
            <form @submit.prevent="searchRecords">
              
              <!-- Amount Range -->
              <div class="mb-3">
                <label class="form-label fw-semibold">Amount Range</label>
                <div class="row g-2">
                  <div class="col-6">
                    <input 
                      type="number" 
                      class="form-control form-control-sm" 
                      v-model="queries.min_amount" 
                      placeholder="Min"
                      step="0.01" 
                      min="0"
                    >
                  </div>
                  <div class="col-6">
                    <input 
                      type="number" 
                      class="form-control form-control-sm" 
                      v-model="queries.max_amount" 
                      placeholder="Max"
                      step="0.01" 
                      min="0"
                    >
                  </div>
                </div>
              </div>

              <!-- Status Filter -->
              <div class="mb-3">
                <label class="form-label fw-semibold">Status</label>
                <select class="form-select form-select-sm" v-model="queries.status">
                  <option value="">All Statuses</option>
                  <option v-for="status in statuses" :key="status" :value="status">
                    {{ status.charAt(0).toUpperCase() + status.slice(1) }}
                  </option>
                </select>
              </div>

              <!-- Payment Method -->
              <div class="mb-3">
                <label class="form-label fw-semibold">Payment Method</label>
                <select class="form-select form-select-sm" v-model="queries.method">
                  <option value="">All Methods</option>
                  <option v-for="method in methods" :key="method" :value="method">
                    {{ formatMethodName(method) }}
                  </option>
                </select>
              </div>

              <!-- Card Specific Filters -->
              <template v-if="queries.method === 'card'">
                <div class="mb-3">
                  <label class="form-label fw-semibold">Card Brand</label>
                  <select class="form-select form-select-sm" v-model="queries.brand">
                    <option value="">All Brands</option>
                    <option v-for="brand in card_brands" :key="brand" :value="brand">
                      {{ brand.charAt(0).toUpperCase() + brand.slice(1) }}
                    </option>
                  </select>
                </div>
                <div class="mb-3">
                  <label class="form-label fw-semibold">Last 4 Digits</label>
                  <input 
                    type="text" 
                    class="form-control form-control-sm" 
                    v-model="queries.last4" 
                    placeholder="1234"
                    maxlength="4"
                  >
                </div>
              </template>

              <!-- Date Range -->
              <div class="mb-3">
                <label class="form-label fw-semibold">Date Range</label>
                <div class="row g-2">
                  <div class="col-12">
                    <input 
                      type="date" 
                      class="form-control form-control-sm" 
                      v-model="queries.from_date"
                      placeholder="From"
                    >
                  </div>
                  <div class="col-12">
                    <input 
                      type="date" 
                      class="form-control form-control-sm" 
                      v-model="queries.to_date"
                      placeholder="To"
                    >
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
                             <div class="d-grid gap-2">
                 <button type="submit" class="btn btn-dark btn-sm" :disabled="processing">
                   <i class="fas fa-search me-1"></i>
                   {{ processing ? 'Searching...' : 'Search' }}
                 </button>
                 <button type="button" class="btn btn-outline-dark btn-sm" @click="clearFilters">
                   <i class="fas fa-times me-1"></i>
                   Clear Filters
                 </button>
               </div>

            </form>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div :class="showFilters ? 'col-lg-9 col-md-12' : 'col-12'">
        
                 <!-- Quick Stats -->
         <div class="row mb-4">
           <div class="col-md-3 col-sm-6 mb-3">
             <div class="card stats-card stats-success">
               <div class="card-body">
                 <div class="d-flex justify-content-between align-items-center">
                   <div>
                     <div class="stats-label">Successful</div>
                     <div class="stats-value">{{ getStatusCount('succeeded') }}</div>
                   </div>
                   <div class="stats-icon stats-icon-success">
                     <i class="fas fa-check-circle"></i>
                   </div>
                 </div>
               </div>
             </div>
           </div>
           <div class="col-md-3 col-sm-6 mb-3">
             <div class="card stats-card stats-pending">
               <div class="card-body">
                 <div class="d-flex justify-content-between align-items-center">
                   <div>
                     <div class="stats-label">Pending</div>
                     <div class="stats-value">{{ getStatusCount('viewed') }}</div>
                   </div>
                   <div class="stats-icon stats-icon-pending">
                     <i class="fas fa-clock"></i>
                   </div>
                 </div>
               </div>
             </div>
           </div>
           <div class="col-md-3 col-sm-6 mb-3">
             <div class="card stats-card stats-failed">
               <div class="card-body">
                 <div class="d-flex justify-content-between align-items-center">
                   <div>
                     <div class="stats-label">Failed</div>
                     <div class="stats-value">{{ getStatusCount('failed') }}</div>
                   </div>
                   <div class="stats-icon stats-icon-failed">
                     <i class="fas fa-times-circle"></i>
                   </div>
                 </div>
               </div>
             </div>
           </div>
           <div class="col-md-3 col-sm-6 mb-3">
             <div class="card stats-card stats-total">
               <div class="card-body">
                 <div class="d-flex justify-content-between align-items-center">
                   <div>
                     <div class="stats-label">Total Amount</div>
                     <div class="stats-value">${{ getTotalAmount() }}</div>
                   </div>
                   <div class="stats-icon stats-icon-total">
                     <i class="fas fa-dollar-sign"></i>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         </div>

        <!-- Transactions Table -->
        <div class="card shadow-sm">
          <div class="card-header bg-light">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                <i class="fas fa-list me-2"></i>
                Recent Transactions
              </h6>
              <div class="d-flex align-items-center gap-2">
                <small class="text-muted">{{ records.length }} of {{ total.toLocaleString() }}</small>
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" @click="refreshRecords">
                    <i class="fas fa-sync-alt"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Transaction ID</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Payment Method</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in records" :key="record.id" class="align-middle">
                    <!-- Transaction ID -->
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="transaction-icon me-2">
                          <i :class="getTransactionIcon(record.method)" class="text-muted"></i>
                        </div>
                        <div>
                          <div class="fw-medium">{{ formatTransactionId(record.payment) }}</div>
                          <small class="text-muted">{{ record.id || 'N/A' }}</small>
                        </div>
                      </div>
                    </td>
                    
                    <!-- Amount -->
                    <td>
                      <div class="fw-semibold">{{ record.amount }}</div>
                    </td>
                    
                    <!-- Status -->
                    <td>
                      <span :class="getStatusBadgeClass(record.status)" class="badge">
                        <i :class="getStatusIcon(record.status)" class="me-1"></i>
                        {{ formatStatus(record.status) }}
                      </span>
                    </td>
                    
                    <!-- Payment Method -->
                    <td>
                      <div class="d-flex align-items-center">
                        <i :class="getMethodIcon(record.method)" class="me-2"></i>
                        <div>
                          <div>{{ formatMethodName(record.method) }}</div>
                          <small class="text-muted" v-if="getPaymentDetails(record)">
                            {{ getPaymentDetails(record) }}
                          </small>
                        </div>
                      </div>
                    </td>
                    
                    <!-- Client -->
                    <td>
                      <div v-if="record.client">
                        {{ record.client }}
                      </div>
                      <span v-else class="text-muted">—</span>
                    </td>
                    
                    <!-- Date -->
                    <td>
                      <div>{{ formatDate(record.created_at) }}</div>
                      <small class="text-muted">{{ formatTime(record.created_at) }}</small>
                    </td>
                    
                                         <!-- Actions -->
                     <td>
                       <div class="btn-group btn-group-sm">
                         <a :href="'/transactions/'+record.uuid" class="btn btn-outline-dark" target="_blank">
                           <i class="fas fa-eye"></i>
                         </a>
                         <a :href="'/transactions/'+record.uuid" class="btn btn-outline-dark">
                           <i class="fas fa-download"></i>
                         </a>
                       </div>
                     </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Empty State -->
            <div v-if="!processing && records && !records.length" class="text-center py-5">
              <i class="fas fa-receipt fa-3x text-muted mb-3"></i>
              <h5 class="text-muted">No transactions found</h5>
              <p class="text-muted">Try adjusting your search filters or check back later.</p>
            </div>
            
                         <!-- Loading State -->
             <div v-if="processing" class="text-center py-5">
               <div class="spinner-border text-dark" role="status">
                 <span class="visually-hidden">Loading...</span>
               </div>
               <p class="text-muted mt-2">Loading transactions...</p>
             </div>
          </div>
          
                     <!-- Load More -->
           <div v-if="records && records.length < total" class="card-footer bg-light text-center">
             <button
               type="button"
               class="btn btn-outline-dark"
               :disabled="processing"
               @click="getRecords"
             >
               <i class="fas fa-plus me-1"></i>
               Load More Transactions
             </button>
           </div>
        </div>
      </div>
    </div>

    <!-- Transaction Details Modal -->
    <div class="modal fade" id="transactionModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Transaction Details</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="selectedTransaction">
            <div class="row">
                             <div class="col-md-6">
                 <h6>Transaction Information</h6>
                 <table class="table table-sm">
                   <tbody>
                     <tr>
                       <td><strong>ID:</strong></td>
                       <td>{{ selectedTransaction.id }}</td>
                     </tr>
                     <tr>
                       <td><strong>Amount:</strong></td>
                       <td>${{ formatAmount(selectedTransaction.amount) }}</td>
                     </tr>
                     <tr>
                       <td><strong>Status:</strong></td>
                       <td>
                         <span :class="getStatusBadgeClass(selectedTransaction.status)" class="badge">
                           {{ formatStatus(selectedTransaction.status) }}
                         </span>
                       </td>
                     </tr>
                     <tr>
                       <td><strong>Method:</strong></td>
                       <td>{{ formatMethodName(selectedTransaction.method) }}</td>
                     </tr>
                     <tr>
                       <td><strong>Date:</strong></td>
                       <td>{{ formatDateTime(selectedTransaction.created_at) }}</td>
                     </tr>
                   </tbody>
                 </table>
               </div>
              <div class="col-md-6">
                <h6>Payment Details</h6>
                <div class="bg-light p-3 rounded">
                  <pre class="mb-0 small">{{ formatPaymentData(selectedTransaction.pay_data) }}</pre>
                </div>
              </div>
            </div>
          </div>
                     <div class="modal-footer">
             <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
             <button type="button" class="btn btn-dark" @click="downloadReceipt(selectedTransaction)">
               <i class="fas fa-download me-1"></i>
               Download Receipt
             </button>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  
  data () {
    return {
      records: [],
      downloads: [],
      processing: false,
      downloading: false,
      total: 0,
      queries: {},
      form: {},
      selectedTransaction: null,
      showFilters: true,
      methods: ['card', 'ach', 'paypal', 'applepay', 'googlepay', 'manual'],
      statuses: ['succeeded', 'viewed', 'failed', 'refunded'],
      card_brands: ['visa', 'mastercard', 'amex', 'discover', 'jcb', 'diners'],
    }
  },

  mounted() {
    var self = this;

    const transactionCreatedHandler = function (data) {
      self.records.unshift(data);
      self.total++;
    };
    
    this.$eventBus.on('transactionCreated', transactionCreatedHandler);
    this.transactionCreatedHandler = transactionCreatedHandler;
    this.refreshRecords();
  },
  
  beforeUnmount() {
    if (this.transactionCreatedHandler) {
      this.$eventBus.off('transactionCreated', this.transactionCreatedHandler);
    }
  },

  created () {
    this.resetRecords();
    this.resetQueries();
    this.resetForm();
    this.setURLParams();
    this.getRecords();
    this.getPlans();
  },

  methods: {

    searchRecords () {
      this.resetRecords();
      this.getRecords();
    },

    refreshRecords() {
      this.resetRecords();
      this.getRecords();
    },

    resetRecords() {
      this.records = [];
      this.queries.skip = 0;
      this.total = 0;
    },

    resetForm() {
      this.form = {
        client: null
      }
    },

    resetQueries() {
      this.queries = {
        limit: 10,
        skip: 0,
        sort: 'created_at',
        sortDirection: 'desc',
        client: null,
        amount: null,
        plan: null,
        min_amount: null,
        max_amount: null,
        date: null,
        from_date: null,
        to_date: null,
        last4: null,
        brand: null,
        status: null,
        method: null
      }
    },

    clearFilters() {
      this.resetQueries();
      this.searchRecords();
    },

    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    updateURL() {
      history.pushState({}, null, this.buildQueries(this.$route.meta.base_url, this.queries));
    },

    getRecords () {
      this.processing = true;
      this.updateURL();
      this.totalRecords();

      this.$http.get(this.buildQueries(this.$route.meta.base_url, this.queries))
      .then(response => {
        if (response && response.data) {
          this.processing = false;

          if(response.data.length) {
            this.queries.skip += response.data.length;
            var self = this;
            response.data.forEach(function(data) {
              self.records.push(data);
            });
          }
        }
      })
      .catch(() => {
        this.processing = false;
      });
    },

    totalRecords() {
      this.$http.get(this.buildQueries(this.$route.meta.base_url+'/total', this.queries))
      .then(response => {
        if(response && response.status === 200) {
          this.total = response.data;
        }
      })
    },

    getPlans() {
      this.$http.get('/pay_plans?fields=uuid,name&limit=100')
      .then(response => {
        if(response && response.data) {
          this.plans = response.data;
        }
      })
    },

    // Formatting Methods
    formatAmount(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toFixed(2);
    },

    formatTransactionId(payment) {
      if (!payment) return 'N/A';
      return payment.substring(0, 12) + '...';
    },

    formatStatus(status) {
      if (!status) return 'Unknown';
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    formatMethodName(method) {
      if (!method) return 'Unknown';
      const methodNames = {
        'card': 'Credit Card',
        'ach': 'Bank Transfer',
        'paypal': 'PayPal',
        'applepay': 'Apple Pay',
        'googlepay': 'Google Pay',
        'manual': 'Manual'
      };
      return methodNames[method] || method.charAt(0).toUpperCase() + method.slice(1);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },

    formatTime(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleTimeString();
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    },

    formatPaymentData(payData) {
      if (!payData) return 'No payment data available';
      try {
        const parsed = typeof payData === 'string' ? JSON.parse(payData) : payData;
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return payData;
      }
    },

    // Status and Icon Methods
    getStatusBadgeClass(status) {
      const classes = {
        'succeeded': 'badge bg-success',
        'viewed': 'badge bg-warning',
        'failed': 'badge bg-danger',
        'refunded': 'badge bg-info'
      };
      return classes[status] || 'badge bg-secondary';
    },

    getStatusIcon(status) {
      const icons = {
        'succeeded': 'fas fa-check',
        'viewed': 'fas fa-clock',
        'failed': 'fas fa-times',
        'refunded': 'fas fa-undo'
      };
      return icons[status] || 'fas fa-question';
    },

    getMethodIcon(method) {
      const icons = {
        'card': 'fas fa-credit-card',
        'ach': 'fas fa-university',
        'paypal': 'fab fa-paypal',
        'applepay': 'fab fa-apple-pay',
        'googlepay': 'fab fa-google-pay',
        'manual': 'fas fa-hand-holding-usd'
      };
      return icons[method] || 'fas fa-money-bill';
    },

    getTransactionIcon(method) {
      return this.getMethodIcon(method);
    },

    // Data Analysis Methods
    getStatusCount(status) {
      return this.records.filter(record => record.status === status).length;
    },

    getTotalAmount() {
      const total = this.records
        .filter(record => record.status === 'succeeded')
        .reduce((sum, record) => {
          // Remove currency symbols and parse the numeric value
          const numericAmount = parseFloat((record.amount || '0').replace(/[^0-9.-]/g, ''));
          return sum + (isNaN(numericAmount) ? 0 : numericAmount);
        }, 0);
      return this.formatAmount(total);
    },

    getPaymentDetails(record) {
      if (!record.pay_data) return null;
      
      try {
        const payData = typeof record.pay_data === 'string' ? JSON.parse(record.pay_data) : record.pay_data;
        
        if (record.method === 'card' && payData.card) {
          const brand = payData.card.brand || '';
          const last4 = payData.card.last4 || '';
          return `${brand.toUpperCase()} •••• ${last4}`;
        }
        
        if (record.method === 'ach' && payData.us_bank_account) {
          const last4 = payData.us_bank_account.last4 || '';
          return `Bank •••• ${last4}`;
        }
        
        return null;
      } catch (e) {
        return null;
      }
    },

    downloadReceipt(transaction) {
      // Implement receipt download logic
      console.log('Downloading receipt for transaction:', transaction.id);
    }
  }
}
</script>

<style scoped>
.transactions-page {
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.page-header {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  padding: 2rem;
  border-radius: 0.375rem;
  margin-bottom: 2rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

/* Stats Cards */
.stats-card {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  transition: all 0.2s ease-in-out;
  background-color: #ffffff;
}

.stats-card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.stats-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.stats-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #212529;
  line-height: 1;
}

.stats-icon {
  width: 48px;
  height: 48px;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.stats-icon-success {
  background-color: #d1e7dd;
  color: #0f5132;
}

.stats-icon-pending {
  background-color: #fff3cd;
  color: #664d03;
}

.stats-icon-failed {
  background-color: #f8d7da;
  color: #721c24;
}

.stats-icon-total {
  background-color: #d1ecf1;
  color: #055160;
}

/* Transaction Icon */
.transaction-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
}

/* Table Styling */
.table {
  font-size: 0.875rem;
}

.table th {
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  padding: 1rem 0.75rem;
}

.table td {
  vertical-align: middle;
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #f1f3f4;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

/* Card Styling */
.card {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  background-color: #ffffff;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #495057;
}

/* Button Styling */
.btn {
  border-radius: 0.375rem;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.25px;
}

.btn-dark {
  background-color: #212529;
  border-color: #212529;
}

.btn-dark:hover {
  background-color: #1c1f23;
  border-color: #1a1e21;
}

.btn-outline-dark {
  color: #212529;
  border-color: #212529;
}

.btn-outline-dark:hover {
  background-color: #212529;
  border-color: #212529;
  color: #ffffff;
}

.btn-group-sm .btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
}

/* Badge Styling */
.badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.25px;
}

.badge.bg-success {
  background-color: #198754 !important;
}

.badge.bg-warning {
  background-color: #ffc107 !important;
  color: #000 !important;
}

.badge.bg-danger {
  background-color: #dc3545 !important;
}

.badge.bg-info {
  background-color: #0dcaf0 !important;
  color: #000 !important;
}

/* Modal Styling */
.modal-content {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.modal-body pre {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  padding: 1rem;
  font-size: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
}

/* Form Controls */
.form-control, .form-select {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.form-control:focus, .form-select:focus {
  border-color: #212529;
  box-shadow: 0 0 0 0.2rem rgba(33, 37, 41, 0.25);
}

.form-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}

/* Loading and Empty States */
.spinner-border {
  width: 2rem;
  height: 2rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .transactions-page {
    padding: 1rem;
  }
  
  .page-header {
    padding: 1.5rem;
  }
  
  .page-header .d-flex {
    flex-direction: column;
    gap: 1rem;
  }
  
  .table-responsive {
    font-size: 0.75rem;
  }
  
  .stats-value {
    font-size: 1.5rem;
  }
  
  .stats-icon {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
}

/* Dark theme compatibility */
@media (prefers-color-scheme: dark) {
  .transactions-page {
    background-color: #1a1a1a;
  }
  
  .page-header,
  .card {
    background-color: #2d2d2d;
    border-color: #404040;
    color: #ffffff;
  }
  
  .table th {
    background-color: #404040;
    color: #ffffff;
  }
  
  .table td {
    border-color: #404040;
    color: #ffffff;
  }
}
</style>
